import React, { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'react-toastify'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import {
  GlobalDriverStyles, PageWrap, Topbar, Body, Toolbar, Search, Stats,
  NewBtn, RefreshBtn, TableWrap, Table, THead, HRow, Row,
  CellName, CellMono, CellCheck, CarrierChips, StatusBadge,
  Actions, IconBtn, Empty, Loading,
  ModalOverlay, ModalBox, Label, Input, TwoCols, CheckRow,
  StatusToggle, Switch, MultiSelectWrap, MSItem, MSEmpty, SelectedCount,
  ModalActions, ModalBtn,
  ConfirmBox, ConfirmIcon, ConfirmTitle, ConfirmMessage, ConfirmActions, ConfirmBtn,
} from './style'

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function fmtDoc(doc) {
  const d = String(doc || '').replace(/\D/g, '')
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  return d
}
function onlyDigits(v) { return String(v || '').replace(/\D/g, '') }

// Mapeia driver vindo da API → formato interno
function apiToLocal(d) {
  const carriers = Array.isArray(d.carriers) ? d.carriers
    : Array.isArray(d.Carriers) ? d.Carriers : []
  return {
    id: d.id,
    name: d.name || '',
    document: d.document || '',
    vehicle_weight_capacity: Number(d.vehicle_weight_capacity ?? d.vehicleWeightCapacity ?? 0),
    order_quantity_capacity: Number(d.order_quantity_capacity ?? d.orderQuantityCapacity ?? 0),
    check_weight: Boolean(d.check_weight ?? d.checkWeight ?? false),
    check_order_quantity: Boolean(d.check_order_quantity ?? d.checkOrderQuantity ?? false),
    status: d.status !== false,
    carriers: carriers.map(c => ({ id: c.id, name: c.name })),
    carrier_ids: carriers.map(c => c.id),
  }
}

const EMPTY_FORM = {
  id: null,
  name: '',
  document: '',
  vehicle_weight_capacity: '',
  order_quantity_capacity: '',
  check_weight: false,
  check_order_quantity: false,
  status: true,
  carrier_ids: [],
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SantoLogDrivers(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  const [drivers, setDrivers] = useState([])
  const [carriers, setCarriers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')

  // Modal de form (criar/editar)
  const [form, setForm] = useState({ ...EMPTY_FORM, open: false })

  // Modal de confirmação genérico (Promise)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: '', message: '', confirmLabel: 'Confirmar', tone: 'warn',
  })
  const confirmResolveRef = useRef(null)
  function askConfirm({ title, message, confirmLabel = 'Confirmar', tone = 'warn' }) {
    return new Promise(resolve => {
      confirmResolveRef.current = resolve
      setConfirmDialog({ open: true, title, message, confirmLabel, tone })
    })
  }
  function closeConfirm(value) {
    setConfirmDialog(d => ({ ...d, open: false }))
    if (confirmResolveRef.current) {
      const r = confirmResolveRef.current
      confirmResolveRef.current = null
      r(Boolean(value))
    }
  }

  // ── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line
  useEffect(() => {
    if (auth === 'authorized') {
      fetchDrivers()
      fetchCarriers()
    }
  }, [auth]) // eslint-disable-line

  async function fetchDrivers() {
    setLoading(true)
    try {
      const res = await apiSantoLog.get('/driver')
      const list = Array.isArray(res.data) ? res.data
        : (res.data?.data || res.data?.drivers || [])
      setDrivers(list.map(apiToLocal))
    } catch (err) {
      console.error('GET /driver failed:', err)
      toast.error('Erro ao carregar motoristas: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setLoading(false)
    }
  }

  async function fetchCarriers() {
    try {
      const res = await apiSantoLog.get('/carrier')
      const list = Array.isArray(res.data) ? res.data
        : (res.data?.data || res.data?.carriers || [])
      setCarriers(list)
    } catch (err) {
      console.error('GET /carrier failed:', err)
      toast.error('Erro ao carregar transportadoras: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    }
  }

  // ── MODAL HANDLERS ────────────────────────────────────────────────────────
  function openCreate() {
    setForm({ ...EMPTY_FORM, open: true })
  }
  function openEdit(d) {
    setForm({
      open: true,
      id: d.id,
      name: d.name,
      document: d.document,
      vehicle_weight_capacity: d.vehicle_weight_capacity || '',
      order_quantity_capacity: d.order_quantity_capacity || '',
      check_weight: !!d.check_weight,
      check_order_quantity: !!d.check_order_quantity,
      status: !!d.status,
      carrier_ids: Array.isArray(d.carrier_ids) ? [...d.carrier_ids] : [],
    })
  }
  function closeForm() { setForm(f => ({ ...f, open: false })) }
  const setField = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), [])
  function toggleCarrier(carrierId) {
    setForm(f => ({
      ...f,
      carrier_ids: f.carrier_ids.includes(carrierId)
        ? f.carrier_ids.filter(id => id !== carrierId)
        : [...f.carrier_ids, carrierId],
    }))
  }

  // ── SAVE ──────────────────────────────────────────────────────────────────
  function buildPayload() {
    return {
      name: form.name.trim(),
      document: onlyDigits(form.document),
      vehicle_weight_capacity: Number(form.vehicle_weight_capacity) || 0,
      order_quantity_capacity: Number(form.order_quantity_capacity) || 0,
      check_weight: !!form.check_weight,
      check_order_quantity: !!form.check_order_quantity,
      status: !!form.status,
      carrier_ids: [...form.carrier_ids],
    }
  }

  function validate() {
    if (!form.name.trim()) { toast.warn('Informe o nome do motorista'); return false }
    if (!onlyDigits(form.document)) { toast.warn('Informe o documento (CPF/CNPJ)'); return false }
    const doc = onlyDigits(form.document)
    if (doc.length !== 11 && doc.length !== 14) {
      toast.warn('Documento deve ter 11 (CPF) ou 14 (CNPJ) dígitos')
      return false
    }
    if (form.carrier_ids.length === 0) {
      toast.warn('Selecione pelo menos uma transportadora')
      return false
    }
    return true
  }

  async function saveDriver() {
    if (!validate()) return
    const payload = buildPayload()
    setSaving(true)
    try {
      if (form.id) {
        const res = await apiSantoLog.put(`/driver/${form.id}`, payload)
        const updated = res.data?.data || res.data || {}
        const merged = apiToLocal({
          ...payload,
          id: form.id,
          carriers: carriers.filter(c => payload.carrier_ids.includes(c.id)),
          ...updated,
        })
        setDrivers(prev => prev.map(d => d.id === form.id ? merged : d))
        toast.success('Motorista atualizado com sucesso')
      } else {
        const res = await apiSantoLog.post('/driver', payload)
        const saved = res.data?.data || res.data || {}
        const created = apiToLocal({
          ...payload,
          id: saved?.id,
          carriers: carriers.filter(c => payload.carrier_ids.includes(c.id)),
          ...saved,
        })
        setDrivers(prev => [created, ...prev])
        toast.success('Motorista cadastrado com sucesso')
      }
      closeForm()
    } catch (err) {
      console.error('Save driver failed:', err)
      toast.error('Erro ao salvar motorista: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setSaving(false)
    }
  }

  // ── DELETE ────────────────────────────────────────────────────────────────
  async function deleteDriver(d) {
    const ok = await askConfirm({
      title: 'Remover motorista',
      message: `Tem certeza que deseja remover "${d.name}"? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Remover',
      tone: 'danger',
    })
    if (!ok) return
    setSaving(true)
    try {
      await apiSantoLog.delete(`/driver/${d.id}`)
      setDrivers(prev => prev.filter(x => x.id !== d.id))
      toast.success('Motorista removido')
    } catch (err) {
      console.error('DELETE /driver/:id failed:', err)
      toast.error('Erro ao remover motorista: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setSaving(false)
    }
  }

  // ── FILTER ────────────────────────────────────────────────────────────────
  const q = query.trim().toLowerCase()
  const filtered = !q ? drivers : drivers.filter(d => {
    const carrierNames = d.carriers.map(c => c.name).join(' ').toLowerCase()
    return d.name.toLowerCase().includes(q)
      || onlyDigits(d.document).includes(onlyDigits(q))
      || carrierNames.includes(q)
  })

  const activeCount = drivers.filter(d => d.status).length

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Motoristas">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalDriverStyles />
          <PageWrap>
            <Topbar>
              <h2>👤 Cadastro de Motoristas</h2>
              <span className="crumb">Gerencie motoristas vinculados às transportadoras</span>
            </Topbar>

            <Body>
              <Toolbar>
                <Search
                  placeholder="Buscar por nome, documento ou transportadora…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                <Stats>
                  <span><strong>{drivers.length}</strong>total</span>
                  <span><strong>{activeCount}</strong>ativos</span>
                </Stats>
                <RefreshBtn onClick={fetchDrivers} disabled={loading}>
                  {loading ? '…' : '↻ Atualizar'}
                </RefreshBtn>
                <NewBtn onClick={openCreate} disabled={saving}>+ Novo motorista</NewBtn>
              </Toolbar>

              <TableWrap>
                {loading && !drivers.length && <Loading>Carregando motoristas…</Loading>}

                {!loading && !drivers.length && (
                  <Empty>
                    Nenhum motorista cadastrado.<br />
                    Clique em <strong>"Novo motorista"</strong> para começar.
                  </Empty>
                )}

                {!loading && drivers.length > 0 && filtered.length === 0 && (
                  <Empty>Nenhum motorista corresponde à busca.</Empty>
                )}

                {drivers.length > 0 && filtered.length > 0 && (
                  <Table>
                    <THead>
                      <HRow>
                        <div>Motorista</div>
                        <div>Peso máx.</div>
                        <div>Pedidos máx.</div>
                        <div>Validações</div>
                        <div>Transportadoras</div>
                        <div>Status</div>
                        <div style={{ textAlign: 'right' }}>Ações</div>
                      </HRow>
                    </THead>

                    {filtered.map(d => (
                      <Row key={d.id}>
                        <CellName>
                          {d.name}
                          <span className="doc">{fmtDoc(d.document) || '—'}</span>
                        </CellName>
                        <CellMono>
                          {d.vehicle_weight_capacity.toLocaleString('pt-BR')}<span className="unit">kg</span>
                        </CellMono>
                        <CellMono>
                          {d.order_quantity_capacity}<span className="unit">peds</span>
                        </CellMono>
                        <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
                          <CellCheck $on={d.check_weight}>peso</CellCheck>
                          <CellCheck $on={d.check_order_quantity}>qtd</CellCheck>
                        </div>
                        <CarrierChips>
                          {d.carriers.slice(0, 3).map(c => (
                            <span key={c.id} className="chip">{c.name}</span>
                          ))}
                          {d.carriers.length > 3 && (
                            <span className="more">+{d.carriers.length - 3}</span>
                          )}
                          {d.carriers.length === 0 && <span className="chip">—</span>}
                        </CarrierChips>
                        <div>
                          <StatusBadge $on={d.status}>
                            {d.status ? 'Ativo' : 'Inativo'}
                          </StatusBadge>
                        </div>
                        <div>
                          <Actions>
                            <IconBtn onClick={() => openEdit(d)} disabled={saving} title="Editar motorista">✏️</IconBtn>
                            <IconBtn $danger onClick={() => deleteDriver(d)} disabled={saving} title="Remover motorista">🗑</IconBtn>
                          </Actions>
                        </div>
                      </Row>
                    ))}
                  </Table>
                )}
              </TableWrap>
            </Body>
          </PageWrap>

          {/* MODAL CRIAR / EDITAR */}
          <ModalOverlay $visible={form.open}>
            <ModalBox>
              <h3>{form.id ? 'Editar motorista' : 'Novo motorista'}</h3>
              <p>Preencha as informações principais e selecione a(s) transportadora(s) que este motorista atende.</p>

              <Label>Nome *</Label>
              <Input
                autoFocus
                value={form.name}
                onChange={e => { const v = e.target.value; setField('name', v) }}
                placeholder="Ex: Amanda Leandro"
                disabled={saving}
              />

              <Label>Documento * <span className="opt">(CPF ou CNPJ — só dígitos)</span></Label>
              <Input
                value={form.document}
                onChange={e => { const v = e.target.value; setField('document', v) }}
                placeholder="11618707418"
                disabled={saving}
                maxLength={18}
              />

              <TwoCols>
                <div>
                  <Label>Capacidade de peso <span className="opt">(kg)</span></Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.001"
                    value={form.vehicle_weight_capacity}
                    onChange={e => { const v = e.target.value; setField('vehicle_weight_capacity', v) }}
                    placeholder="1000"
                    disabled={saving}
                  />
                </div>
                <div>
                  <Label>Qtd. máxima de pedidos</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.order_quantity_capacity}
                    onChange={e => { const v = e.target.value; setField('order_quantity_capacity', v) }}
                    placeholder="20"
                    disabled={saving}
                  />
                </div>
              </TwoCols>

              <Label>Validações automáticas <span className="opt">(opcional)</span></Label>
              <TwoCols>
                <CheckRow $checked={form.check_weight}>
                  <input
                    type="checkbox"
                    checked={form.check_weight}
                    onChange={e => { const v = e.target.checked; setField('check_weight', v) }}
                    disabled={saving}
                  />
                  <div>
                    <div className="lbl">Validar peso</div>
                    <div className="hint">Bloquear atribuição quando exceder a capacidade de peso</div>
                  </div>
                </CheckRow>
                <CheckRow $checked={form.check_order_quantity}>
                  <input
                    type="checkbox"
                    checked={form.check_order_quantity}
                    onChange={e => { const v = e.target.checked; setField('check_order_quantity', v) }}
                    disabled={saving}
                  />
                  <div>
                    <div className="lbl">Validar qtd. de pedidos</div>
                    <div className="hint">Bloquear atribuição quando exceder o limite de pedidos</div>
                  </div>
                </CheckRow>
              </TwoCols>

              <Label>Transportadoras *</Label>
              <MultiSelectWrap>
                {carriers.length === 0 ? (
                  <MSEmpty>Nenhuma transportadora cadastrada.</MSEmpty>
                ) : (
                  carriers.map(c => {
                    const checked = form.carrier_ids.includes(c.id)
                    return (
                      <MSItem key={c.id} $checked={checked}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCarrier(c.id)}
                          disabled={saving}
                        />
                        <span className="name">{c.name}</span>
                        {c.document && <span className="doc">{fmtDoc(c.document)}</span>}
                      </MSItem>
                    )
                  })
                )}
              </MultiSelectWrap>
              <SelectedCount>
                <span>{form.carrier_ids.length}</span> transportadora{form.carrier_ids.length !== 1 ? 's' : ''} selecionada{form.carrier_ids.length !== 1 ? 's' : ''}
              </SelectedCount>

              <Label>Status</Label>
              <StatusToggle>
                <div>
                  <div className="lbl">{form.status ? 'Motorista ativo' : 'Motorista inativo'}</div>
                  <span className="hint">
                    {form.status
                      ? 'Disponível para receber pedidos e ser vinculado a áreas'
                      : 'Não receberá novos pedidos enquanto estiver inativo'}
                  </span>
                </div>
                <Switch
                  $on={form.status}
                  onClick={() => setField('status', !form.status)}
                  disabled={saving}
                  type="button"
                />
              </StatusToggle>

              <ModalActions>
                <ModalBtn onClick={closeForm} disabled={saving}>Cancelar</ModalBtn>
                <ModalBtn $primary onClick={saveDriver} disabled={saving}>
                  {saving ? 'Salvando…' : (form.id ? 'Salvar alterações' : 'Cadastrar motorista')}
                </ModalBtn>
              </ModalActions>
            </ModalBox>
          </ModalOverlay>

          {/* CONFIRM MODAL */}
          <ModalOverlay $visible={confirmDialog.open}>
            <ConfirmBox $tone={confirmDialog.tone}>
              <ConfirmIcon $tone={confirmDialog.tone}>
                {confirmDialog.tone === 'danger' ? '⚠' : '?'}
              </ConfirmIcon>
              <ConfirmTitle>{confirmDialog.title}</ConfirmTitle>
              <ConfirmMessage>{confirmDialog.message}</ConfirmMessage>
              <ConfirmActions>
                <ConfirmBtn onClick={() => closeConfirm(false)}>Cancelar</ConfirmBtn>
                <ConfirmBtn $primary $tone={confirmDialog.tone} onClick={() => closeConfirm(true)}>
                  {confirmDialog.confirmLabel}
                </ConfirmBtn>
              </ConfirmActions>
            </ConfirmBox>
          </ModalOverlay>
        </>
      )}
    </Dashboard>
  )
}
